import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Sofa, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, Building2, Factory } from 'lucide-react';

const CommercialCarpetCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "Do you offer steam cleaning or dry cleaning?",
      a: "Both. We choose the right method for your carpet based on its type and condition, using hired equipment suited to the job."
    },
    {
      q: "Is this a one-off service or can it be scheduled regularly?",
      a: "Both. We provide one-off carpet cleaning for lease handovers and deep cleans, as well as scheduled recurring carpet cleaning for occupied offices."
    },
    {
      q: "Can you clean carpet as part of an end-of-lease handover?",
      a: "Yes, carpet cleaning is commonly included as part of a full end-of-lease warehouse or office turnover, alongside floors, windows and amenities."
    },
    {
      q: "How often should commercial carpet be cleaned?",
      a: "It depends on foot traffic and use. High-traffic areas generally need more frequent cleaning. We can recommend a schedule based on your site."
    }
  ];

  const relatedServices = [
    {
      title: 'Commercial Office Cleaning',
      description: 'Full office cleaning including carpets, windows and amenities.',
      link: '/services/commercial-office-cleaning-perth',
      icon: Building2
    },
    {
      title: 'Warehouse & Industrial Cleaning',
      description: 'Carpet cleaning as part of a complete warehouse end-of-lease turnover.',
      link: '/services/warehouse-industrial-cleaning-perth',
      icon: Factory
    }
  ];

  // Service Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Commercial Carpet Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep", "Leederville", "Northbridge", 
      "Mount Lawley", "Fremantle", "Cockburn", "Landsdale", "Hope Valley", 
      "Kalgoorlie", "Esperance", "Albany", "Busselton", "Bunbury", "Margaret River", "Australind", "Geraldton"
    ],
    "description": "Steam and dry carpet cleaning for offices, warehouses and commercial properties. Scheduled cleaning or one-off jobs for lease handovers and deep cleans."
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/services/commercial-carpet-cleaning-perth" />
        <title>Commercial Carpet Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Steam and dry carpet cleaning for offices, warehouses and commercial properties across Perth and WA. Scheduled cleaning or one-off jobs. 20+ years experience." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="commercial-carpet-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1781637590564-01c65dbf2039?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwY2FycGV0JTIwY2xlYW5pbmclMjBtYWNoaW5lJTIwb2ZmaWNlfGVufDB8fHx8MTc4OTM1MzQ3Mnww&ixlib=rb-4.1.0&q=85"
              alt="Professional carpet cleaning in commercial office"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Sofa size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Carpet Cleaning</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Commercial Carpet Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Steam and dry carpet cleaning for offices, warehouses and commercial properties
              </p>
              <p className="text-gray-400 mb-8">
                Commercial carpet takes a different kind of clean to a household rug, higher foot traffic, larger areas, and often a deadline, whether that's a lease handover or simply keeping an occupied office presentable. Extreme Clean Australia provides both steam and dry carpet cleaning across Perth and WA, backed by 20+ years in commercial and industrial cleaning.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                We offer both steam cleaning and dry cleaning depending on the carpet type, condition and what the job needs, using hired equipment suited to the specific site. This covers everything from a single office floor to a full commercial property, whether it's a scheduled clean for an occupied space or part of an end-of-lease handover.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Steam carpet cleaning',
                  'Dry carpet cleaning',
                  'Office floor cleaning',
                  'Commercial property cleaning',
                  'Scheduled recurring cleans',
                  'End-of-lease deep cleans'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Scheduled Cleaning or One-Off Jobs */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Flexibility</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Scheduled Cleaning or One-Off Jobs</h2>
              <p className="text-gray-600 mb-8">
                Commercial carpet cleaning works both ways for us: a regular scheduled service to keep an occupied office looking its best, or a one-off deep clean as part of a lease handover or warehouse turnover. We scope the job around what the carpet and the situation actually need, not a fixed package.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Recurring Schedule', 'One-Off Deep Clean', 'Lease Handover', 'Warehouse Turnover'].map((option, index) => (
                  <span key={index} className="bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                    {option}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Why Us</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Why Businesses Choose Extreme Clean Australia</h2>
              <p className="text-gray-300">
                Getting genuine results from commercial carpet, not just a surface refresh, takes the right method and the right equipment for the carpet in question. Backed by 20+ years across commercial, food-service and industrial sites in WA, we bring that same standard to carpet cleaning as we do to every other part of the business.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Perth Metro</h3>
                  <p className="text-gray-600">
                    We service the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Osborne Park, Malaga, Kewdale, Welshpool, Canning Vale, Bibra Lake, Jandakot, Wangara, Balcatta, Bayswater, Rockingham, Joondalup and Midland.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Regional WA</h3>
                  <p className="text-gray-600">
                    Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River, Australind, Geraldton.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your Carpet Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for commercial carpet cleaning, whether it's a one-off deep clean or a recurring schedule.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CommercialCarpetCleaning;
